import { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import * as styles from './styles';
import { WelcomeViewProps } from './models';
import { ActionButton } from '@sunbit-dev/purchase-common/src/components/actionButton/ActionButton';
import { ButtonSize } from '@sunbit-dev/web-ui-lib';
import { useTranslation } from '@sunbit-dev/translations';
import { useTrack } from '~/hooks/track';
import { EventsNames } from '~/enums/Events';
import { PagesTestIds } from '~/enums/TestIds';
import { TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { Title } from './components/title/Title';
import { InvoiceInfo } from './components/invoiceInfo/InvoiceInfo';
import { HowItWorks } from './components/howItWorks/HowItWorks';
import { LegalAuthorization } from './components/legalAuthorization/LegalAuthorization';
import { DisclaimerNote } from './components/disclaimerNote/DisclaimerNote';
import { PrivacyAllowanceLayer } from '@sunbit-dev/purchase-common/src/components/privacyAllowanceLayer/PrivacyAllowanceLayer';
import infoImage from '../../../../../../assets/info-outline-light.svg';

export const WelcomeView = styled(({ className, onStartApplication }: WelcomeViewProps) => {
  const { track } = useTrack();
  const { t } = useTranslation('landing_page', { keyPrefix: 'welcome_view' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const rootDiv = useRef<HTMLDivElement>(null);
  const legalContentEnd = useRef<HTMLDivElement>(null);

  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(false);
  const [shouldDisplayLegalScrollNotice, setShouldDisplayLegalScrollNotice] = useState(false);

  useEffect(() => {
    track({ eventName: EventsNames.welcomeView, type: TrackCategory.View });
  }, []);

  useEffect(() => {
    if (hasReachedBottom) setShouldDisplayLegalScrollNotice(false);
  }, [hasReachedBottom]);

  const onSubmitClick = () => {
    if (hasReachedBottom) {
      setIsSubmitting(true);
      onStartApplication();
    } else {
      setShouldDisplayLegalScrollNotice(true);
    }
  };

  useEffect(() => {
    if (rootDiv?.current && legalContentEnd?.current && !isScrollListenerActive) {
      setIsScrollListenerActive(true);

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setHasReachedBottom(true);
            observer.disconnect();
          }
        },
        { threshold: 1 }
      );

      observer.observe(legalContentEnd.current);

      return () => observer.disconnect();
    }
  }, []);

  const actionButton = useMemo(() => {
    const button = (
      <ActionButton
        dataDdAction={EventsNames.submitPage}
        className="submit-button"
        dataTestId="submit-button"
        size={ButtonSize.small}
        buttonText={t('submit_button')}
        submit={onSubmitClick}
        disabled={isSubmitting || !hasReachedBottom}
      />
    );

    return hasReachedBottom ? (
      button
    ) : (
      <div className="submit-button-container" onClick={onSubmitClick}>
        {button}
        <div className="overlay" />
      </div>
    );
  }, [hasReachedBottom, onSubmitClick, isSubmitting, t]);

  return (
    <PrivacyAllowanceLayer>
      <div data-testid={PagesTestIds.WelcomeView} className={classNames(className, 'view-wrapper')} ref={rootDiv}>
        <Title />
        <InvoiceInfo />
        <HowItWorks className="how-it-works" />
        <div className="legal-section">
          <LegalAuthorization className="legal-authorization" />
          <DisclaimerNote className="disclaimer-note" />
        </div>
        <div ref={legalContentEnd} />
      </div>
      <div className={classNames(className, 'button-container')}>
        {shouldDisplayLegalScrollNotice && (
          <div className="scroll-notice">
            <img className="info-icon" src={infoImage} alt="info-icon" />
            {t('scroll_notice')}
          </div>
        )}
        <div className="sticky-button-wrapper">{actionButton}</div>
      </div>
      <div className={classNames(className, 'button-placeholder')} />
    </PrivacyAllowanceLayer>
  );
})`
  ${styles.WelcomeView}
`;
